import {useCallback, useMemo} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isEmpty} from 'ramda';

import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';
import {DeliveryNoteField} from '../types/DeliveryNoteField';

interface UseMasterCheckboxProps {
  formApi: UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>;
  currentPageItems: DeliveryNoteField[];
}

export interface UseMasterCheckboxReturnType {
  isAllSelected: boolean;
  isIndeterminate: boolean;
  onToggleAll: VoidFunction;
}

export function useMasterCheckbox(props: UseMasterCheckboxProps): UseMasterCheckboxReturnType {
  const listOfItems = useWatch({
    control: props.formApi.control,
    name: 'listOfItems',
  });

  const selectionState = useMemo(() => {
    if (isEmpty(props.currentPageItems)) {
      return {isAllSelected: false, isIndeterminate: false};
    }

    const currentPageSelectedValues = props.currentPageItems.map(
      (item) => listOfItems[item.originalIndex]?.isSelected === true
    );

    const selectedCount = currentPageSelectedValues.filter(Boolean).length;
    const totalCount = currentPageSelectedValues.length;

    const isAllSelected = selectedCount === totalCount && totalCount > 0;
    const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

    return {isAllSelected, isIndeterminate};
  }, [props.currentPageItems, listOfItems]);

  const handleToggleAll = useCallback(() => {
    const shouldSelectAll = !selectionState.isAllSelected;
    props.currentPageItems.forEach((item) => {
      props.formApi.setValue(`listOfItems.${item.originalIndex}.isSelected`, shouldSelectAll);
    });
  }, [selectionState.isAllSelected, props.currentPageItems, props.formApi]);

  return {
    isAllSelected: selectionState.isAllSelected,
    isIndeterminate: selectionState.isIndeterminate,
    onToggleAll: handleToggleAll,
  };
}
